import { Chip } from '@mui/material';
import { Cancel } from '@mui/icons-material';

import { translateStatus } from '../../../../config/constants';

import { SelectAutocomplete } from '../../../../components';

import './FiltersSection.css';
import { FiltersSectionProps } from './FilterSection.types';

/**
 * Filters section component providing comprehensive filtering capabilities
 * Includes regional office, province, brand, and status filters with active filter chips
 *
 * Features:
 * - Autocomplete dropdowns for all filter types
 * - Active filter chips with remove functionality
 * - Status translation for user-friendly display
 * - Responsive design for mobile and desktop
 * - Clear buttons for multi-select filters
 *
 * @param props - Component props containing filter data and callbacks
 * @returns JSX element containing the complete filters interface
 */
export function FiltersSection({
  isMobile,
  regionalOffices,
  provinces,
  brands,
  statuses,
  filters,
  activeFilters,
  onChangeFilter,
  onRemoveFilter
}: FiltersSectionProps) {
  const sortedActiveFilters = [...activeFilters].sort((a: any, b: any) => {
    const filterOrder = ['regional_office', 'province', 'brand', 'status'];
    const aIndex = filterOrder.indexOf(a.key);
    const bIndex = filterOrder.indexOf(b.key);
    return aIndex - bIndex;
  });

  // normalize onRemoveFilter to support both signatures:
  //  - old (curried) form: (key) => (value?) => void
  //  - new direct form: (key, value?) => void
  const handleRemove = (key: string, value?: string) => {
    const fn = onRemoveFilter as any;
    try {
      if (typeof fn === 'function') {
        if (fn.length === 1) {
          // curried: fn(key)(value)
          const maybeFn = fn(key);
          if (typeof maybeFn === 'function') maybeFn(value);
        } else {
          // direct: fn(key, value)
          fn(key, value);
        }
      }
    } catch {
      // no-op on error to avoid breaking UI
    }
  };

  return (
    <div className={`azk-filters-section ${isMobile ? 'mobile' : ''}`}>
      <div className={`filters-row ${isMobile ? 'mobile' : ''}`}>
        <div className="azk-list-filter-field-group">
          <div className="azk-list-filter-label">Регіональне відділення</div>
          <SelectAutocomplete
            options={regionalOffices}
            value={filters.regional_office || ''}
            placeholder="Оберіть регіональне відділення..."
            onChange={onChangeFilter('regional_office')}
          />
        </div>

        <div className="azk-list-filter-field-group">
          <div className="azk-list-filter-label">Область</div>
          <SelectAutocomplete
            options={provinces}
            value={''}
            showClearButton={Array.isArray(filters.province) && filters.province.length > 0}
            placeholder="Оберіть область..."
            onChange={onChangeFilter('province')}
            onClear={() => handleRemove('province')}
          />
        </div>

        <div className="azk-list-filter-field-group">
          <div className="azk-list-filter-label">Бренд</div>
          <SelectAutocomplete
            options={brands}
            value={''}
            showClearButton={Array.isArray(filters.brand) && filters.brand.length > 0}
            placeholder="Оберіть бренд..."
            onChange={onChangeFilter('brand')}
            onClear={() => handleRemove('brand')}
          />
        </div>

        <div className="azk-list-filter-field-group">
          <div className="azk-list-filter-label">Статус АЗК</div>
          <SelectAutocomplete
            options={statuses.map((status: string) => (status === 'Всі' ? status : translateStatus(status)))}
            value={''}
            placeholder="Оберіть статус..."
            showClearButton={
              filters.status && (Array.isArray(filters.status) ? filters.status.length > 0 : !!filters.status)
            }
            onClear={() => handleRemove('status')}
            onChange={onChangeFilter('status')}
          />
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="azk-list-active-filters-container">
          {sortedActiveFilters.map((filter: any, index: number) => (
            <Chip
              key={`${filter.key}-${filter.value}-${index}`}
              label={filter.key === 'status' ? translateStatus(filter.value) : filter.value}
              onDelete={() => handleRemove(filter.key, filter.value)}
              deleteIcon={<Cancel />}
              className={`azk-list-filter-chip ${filter.key}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
